import React, { useState, useRef, useEffect } from 'react';
import {config} from '../config';

// Variable Picker Component
const VariablePicker = ({ variables, position, searchTerm, onSelect, onClose }) => {
  const menuRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  
  const colors = config.colors;

  const filteredVars = Object.entries(variables).filter(([key, val]) =>
    key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(val.value).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset selected index when filtered vars change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  // Adjust position based on available space
  useEffect(() => {
    if (!position || !menuRef.current) return;

    const menuHeight = menuRef.current.offsetHeight;
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - position.top;
    const spaceAbove = position.top;

    let newPosition = { ...position };

    // Prefer below, but if not enough space, check above
    if (spaceBelow < menuHeight + 20 && spaceAbove > spaceBelow) {
      // Position above the cursor
      newPosition.top = position.top - menuHeight - 5;
    }

    setAdjustedPosition(newPosition);
  }, [position, filteredVars]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredVars.length - 1 ? prev + 1 : prev
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (filteredVars[selectedIndex]) {
          onSelect(filteredVars[selectedIndex][0]);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filteredVars, selectedIndex, onSelect, onClose]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (menuRef.current) {
      const selectedElement = menuRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!adjustedPosition) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-50 rounded-lg shadow-xl max-h-60 overflow-y-auto"
      style={{
        top: `${adjustedPosition.top}px`,
        left: `${adjustedPosition.left}px`,
        minWidth: '250px',
        backgroundColor: colors.pickerBg,
        border: `2px solid ${colors.pickerBorder}`
      }}
    >
      {filteredVars.length > 0 ? (
        filteredVars.map(([key, val], index) => (
          <button
            key={key}
            onClick={() => onSelect(key)}
            className="w-full px-4 py-3 text-left transition-colors flex items-center justify-between"
            style={{
              backgroundColor: index === selectedIndex ? colors.pickerSelected : 'transparent',
              borderLeft: index === selectedIndex ? `4px solid ${colors.pickerSelectedBorder}` : 'none'
            }}
            onMouseEnter={(e) => {
              if (index !== selectedIndex) {
                e.currentTarget.style.backgroundColor = colors.pickerHover;
              }
            }}
            onMouseLeave={(e) => {
              if (index !== selectedIndex) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <span 
              className="font-mono text-sm" 
              style={{ color: colors.textSecondary }}
            >
              {key}
            </span>
            <span 
              className="px-2 py-1 rounded text-xs font-medium"
              style={{
                backgroundColor: colors.nodeBg,
                border: `1px solid ${colors.nodeBorder}`,
                color: colors.textPrimary
              }}
            >
              {String(val.value)}
            </span>
          </button>
        ))
      ) : (
        <div 
          className="px-4 py-3 text-sm"
          style={{ color: colors.textMuted }}
        >
          No variables found
        </div>
      )}
    </div>
  );
};

export default VariablePicker;